/**
 * Gift Plate Section Component
 *
 * Complete gift plate designer with:
 * - Live preview at the top (sticky on desktop)
 * - Numbered step-by-step configuration
 * - Background options (solid colors + predefined images)
 * - Text & frame color selector
 * - Optional vertical date
 * - Hole configuration
 * - Symbol picker panel
 */

import { useState } from 'react'
import { motion } from 'framer-motion'
import GiftPlatePreview from './GiftPlatePreview'
import SymbolPicker from './SymbolPicker'
import { useCartStore } from '@/store/cartStore'
import Button from '@/components/common/Button'
import toast from 'react-hot-toast'

const SOLID_COLORS = [
  { name: 'Weiß', value: '#FFFFFF' },
  { name: 'Schwarz', value: '#000000' },
  { name: 'Blau', value: '#0066CC' },
  { name: 'Rosa', value: '#FF69B4' },
]

const BACKGROUND_IMAGES = [
  { id: 'grass', name: 'Gras / Rasen', value: 'linear-gradient(135deg, #7cb342 0%, #558b2f 100%)' },
  { id: 'carbon', name: 'Carbon Fiber', value: 'repeating-linear-gradient(45deg, #1a1a1a 0px, #1a1a1a 2px, #0d0d0d 2px, #0d0d0d 4px)' },
  { id: 'marble', name: 'Marmor', value: 'linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 50%, #9e9e9e 100%)' },
  { id: 'brushed', name: 'Gebürstetes Metall', value: 'linear-gradient(90deg, #c0c0c0 0%, #a0a0a0 50%, #c0c0c0 100%)' },
  { id: 'dark-texture', name: 'Dunkle Textur', value: 'radial-gradient(circle, #2a2a2a 0%, #1a1a1a 100%)' },
  { id: 'light-texture', name: 'Helle Textur', value: 'radial-gradient(circle, #f5f5f5 0%, #e0e0e0 100%)' },
]

const TEXT_COLORS = [
  { name: 'Schwarz', value: '#000000' },
  { name: 'Weiß', value: '#FFFFFF' },
  { name: 'Rot', value: '#FF0000' },
  { name: 'Blau', value: '#0066CC' },
  { name: 'Grün', value: '#00AA00' },
  { name: 'Gold', value: '#FFD700' },
  { name: 'Silber', value: '#C0C0C0' },
]

const HOLE_POSITIONS = [
  { id: 'top-left', label: 'Oben Links' },
  { id: 'top-center', label: 'Oben Mitte' },
  { id: 'top-right', label: 'Oben Rechts' },
  { id: 'center-left', label: 'Mitte Links' },
  { id: 'center', label: 'Mitte' },
  { id: 'center-right', label: 'Mitte Rechts' },
  { id: 'bottom-left', label: 'Unten Links' },
  { id: 'bottom-center', label: 'Unten Mitte' },
  { id: 'bottom-right', label: 'Unten Rechts' },
]

// Step indicator component
const StepNumber = ({ number, label }: { number: number; label: string }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0 shadow-lg shadow-primary-500/30">
      {number}
    </div>
    <h3 className="text-sm sm:text-base font-semibold text-gray-200">{label}</h3>
  </div>
)

const GiftPlateSection = () => {
  const { addItem } = useCartStore()

  const [text, setText] = useState('')
  const [backgroundType, setBackgroundType] = useState<'solid' | 'image'>('solid')
  const [backgroundColor, setBackgroundColor] = useState('#FFFFFF')
  const [backgroundImage, setBackgroundImage] = useState<string | null>(null)
  const [textColor, setTextColor] = useState('#000000')
  const [showDate, setShowDate] = useState(false)
  const [date, setDate] = useState({ day: 1, month: 1, year: 2024 })
  const [datePosition, setDatePosition] = useState<'left' | 'right'>('right')
  const [holesEnabled, setHolesEnabled] = useState(false)
  const [holeCount, setHoleCount] = useState<1 | 2 | 4>(2)
  const [holePositions, setHolePositions] = useState<string[]>(['top-left', 'top-right'])

  const frameColor = textColor

  const handleTextChange = (newText: string) => {
    if (newText.length <= 10) {
      const uppercaseText = newText.split('').map(char =>
        /[a-zA-Z]/.test(char) ? char.toUpperCase() : char
      ).join('')
      setText(uppercaseText)
    }
  }

  const handleSymbolClick = (symbol: string) => {
    if (text.length + symbol.length <= 10) {
      setText(text + symbol)
    } else {
      toast.error('Maximale Zeichenanzahl erreicht (10)')
    }
  }

  const handleBackgroundImageSelect = (image: typeof BACKGROUND_IMAGES[0]) => {
    setBackgroundImage(image.value)
    setBackgroundType('image')
  }

  const toggleHolePosition = (positionId: string) => {
    if (!holesEnabled) return
    setHolePositions((prev) => {
      if (prev.includes(positionId)) return prev.filter((p) => p !== positionId)
      if (prev.length >= holeCount) return prev.slice(1).concat(positionId)
      return [...prev, positionId]
    })
  }

  const handleHoleCountChange = (count: 1 | 2 | 4) => {
    setHoleCount(count)
    if (count === 1) setHolePositions(holePositions.slice(0, 1))
    else if (count === 2) setHolePositions(holePositions.slice(0, 2))
    else if (count === 4) {
      if (holePositions.length < 4)
        setHolePositions(['top-left', 'top-right', 'bottom-left', 'bottom-right'])
    }
  }

  const handleAddToCart = () => {
    if (!text.trim()) {
      toast.error('Bitte geben Sie einen Text ein')
      return
    }

    addItem({
      productId: `gift-plate-${Date.now()}`,
      productName: `Geschenkkennzeichen - ${text}`,
      price: 19.99,
      quantity: 1,
      customization: {
        type: 'gift-plate',
        text,
        backgroundColor,
        backgroundImage,
        backgroundType,
        textColor,
        frameColor,
        showDate,
        date: showDate ? date : undefined,
        datePosition,
        holesEnabled,
        holeCount,
        holePositions,
      },
    })

    toast.success('Geschenkkennzeichen zum Warenkorb hinzugefügt')
  }

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-dark-900 rounded-2xl border border-gray-800"
      >
        {/* Header */}
        <div className="text-center pt-6 sm:pt-8 px-6">
          <div className="flex items-center justify-center mb-4">
            <div className="p-3 bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl text-white shadow-lg shadow-pink-500/30 text-3xl">
              🎁
            </div>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gradient mb-2">
            Geschenkkennzeichen
          </h2>
          <p className="text-gray-400 text-sm sm:text-base leading-relaxed max-w-xl mx-auto">
            Erstellen Sie ein personalisiertes Geschenkkennzeichen mit Ihrem eigenen Text, Emojis und Design
          </p>
        </div>

        {/* ═══════════════════════════════════════════════════════════
            LIVE PREVIEW - Always visible at top
            ═══════════════════════════════════════════════════════════ */}
        <div className="px-4 sm:px-6 pt-6 lg:sticky lg:top-4 lg:z-20">
          <GiftPlatePreview
            text={text}
            onTextChange={handleTextChange}
            backgroundColor={backgroundColor}
            backgroundImage={backgroundImage}
            textColor={textColor}
            frameColor={frameColor}
            showDate={showDate}
            date={showDate ? date : undefined}
            datePosition={datePosition}
            holesEnabled={holesEnabled}
            holePositions={holePositions}
          />
        </div>

        {/* ═══════════════════════════════════════════════════════════
            CONFIGURATION - Two column layout
            ═══════════════════════════════════════════════════════════ */}
        <div className="p-4 sm:p-6 lg:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column: Steps 1-4 */}
            <div className="lg:col-span-2 space-y-6">

              {/* Step 1: Background */}
              <div className="bg-dark-800/50 rounded-xl p-4 sm:p-5 border border-gray-800/50">
                <StepNumber number={1} label="Hintergrund wählen" />

                <div className="flex gap-2 mb-4">
                  <button
                    onClick={() => setBackgroundType('solid')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      backgroundType === 'solid'
                        ? 'bg-primary-500/20 text-primary-400 ring-1 ring-primary-500/50'
                        : 'bg-dark-700/50 text-gray-400 hover:bg-dark-700'
                    }`}
                  >
                    Farbe
                  </button>
                  <button
                    onClick={() => setBackgroundType('image')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      backgroundType === 'image'
                        ? 'bg-primary-500/20 text-primary-400 ring-1 ring-primary-500/50'
                        : 'bg-dark-700/50 text-gray-400 hover:bg-dark-700'
                    }`}
                  >
                    Textur / Bild
                  </button>
                </div>

                {backgroundType === 'solid' ? (
                  <div className="grid grid-cols-4 gap-3">
                    {SOLID_COLORS.map((color) => (
                      <button
                        key={color.value}
                        onClick={() => { setBackgroundColor(color.value); setBackgroundImage(null) }}
                        className={`flex flex-col items-center gap-1.5 p-2 rounded-lg transition-all ${
                          backgroundColor === color.value && !backgroundImage
                            ? 'ring-2 ring-primary-500 bg-dark-700/50'
                            : 'hover:bg-dark-700/30'
                        }`}
                      >
                        <div
                          className={`w-full h-10 rounded-md border ${
                            color.value === '#FFFFFF' ? 'border-gray-500' : 'border-gray-700'
                          }`}
                          style={{ backgroundColor: color.value }}
                        />
                        <span className="text-xs text-gray-400">{color.name}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {BACKGROUND_IMAGES.map((image) => (
                      <button
                        key={image.id}
                        onClick={() => handleBackgroundImageSelect(image)}
                        className={`h-14 rounded-lg transition-all relative overflow-hidden ${
                          backgroundImage === image.value
                            ? 'ring-2 ring-primary-500'
                            : 'ring-1 ring-gray-700 hover:ring-gray-500'
                        }`}
                        style={{ background: image.value }}
                      >
                        <span className="absolute inset-x-0 bottom-0 text-[10px] text-white font-semibold bg-black/60 px-1.5 py-0.5 text-center">
                          {image.name}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Step 2: Text & Frame Color */}
              <div className="bg-dark-800/50 rounded-xl p-4 sm:p-5 border border-gray-800/50">
                <StepNumber number={2} label="Text- & Rahmenfarbe" />
                <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                  {TEXT_COLORS.map((color) => (
                    <button
                      key={color.value}
                      onClick={() => setTextColor(color.value)}
                      className={`flex flex-col items-center gap-1.5 p-1.5 rounded-lg transition-all ${
                        textColor === color.value
                          ? 'ring-2 ring-primary-500 bg-dark-700/50'
                          : 'hover:bg-dark-700/30'
                      }`}
                    >
                      <div
                        className={`w-full h-9 rounded-md border ${
                          color.value === '#FFFFFF' ? 'border-gray-500' : 'border-gray-700'
                        }`}
                        style={{ backgroundColor: color.value }}
                      />
                      <span className="text-[10px] text-gray-400">{color.name}</span>
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-gray-500 mt-2">
                  Rahmenfarbe passt sich automatisch an die Textfarbe an
                </p>
              </div>

              {/* Step 3: Date (optional) */}
              <div className="bg-dark-800/50 rounded-xl p-4 sm:p-5 border border-gray-800/50">
                <StepNumber number={3} label="Datum (optional)" />

                <label className="flex items-center gap-3 cursor-pointer group" onClick={() => setShowDate(!showDate)}>
                  <div className={`relative w-10 h-5 rounded-full transition-colors ${showDate ? 'bg-primary-500' : 'bg-dark-600'}`}>
                    <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${showDate ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </div>
                  <span className="text-sm text-gray-300 group-hover:text-gray-200 transition-colors">Datum anzeigen</span>
                </label>

                {showDate && (
                  <div className="mt-4 space-y-4">
                    <div>
                      <label className="block text-xs text-gray-400 mb-2">Position</label>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setDatePosition('left')}
                          className={`px-4 py-1.5 rounded-lg text-sm transition-all ${
                            datePosition === 'left'
                              ? 'bg-primary-500/20 text-primary-400 ring-1 ring-primary-500/50'
                              : 'bg-dark-700/50 text-gray-400 hover:bg-dark-700'
                          }`}
                        >
                          Links
                        </button>
                        <button
                          onClick={() => setDatePosition('right')}
                          className={`px-4 py-1.5 rounded-lg text-sm transition-all ${
                            datePosition === 'right'
                              ? 'bg-primary-500/20 text-primary-400 ring-1 ring-primary-500/50'
                              : 'bg-dark-700/50 text-gray-400 hover:bg-dark-700'
                          }`}
                        >
                          Rechts
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Tag</label>
                        <input
                          type="number"
                          min="1"
                          max="31"
                          value={date.day}
                          onChange={(e) => setDate({ ...date, day: parseInt(e.target.value) || 1 })}
                          className="w-full px-3 py-2 bg-dark-800 border border-gray-700 rounded-lg text-white text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Monat</label>
                        <input
                          type="number"
                          min="1"
                          max="12"
                          value={date.month}
                          onChange={(e) => setDate({ ...date, month: parseInt(e.target.value) || 1 })}
                          className="w-full px-3 py-2 bg-dark-800 border border-gray-700 rounded-lg text-white text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Jahr</label>
                        <input
                          type="number"
                          min="1900"
                          max="2100"
                          value={date.year}
                          onChange={(e) => setDate({ ...date, year: parseInt(e.target.value) || 2024 })}
                          className="w-full px-3 py-2 bg-dark-800 border border-gray-700 rounded-lg text-white text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Step 4: Holes (optional) */}
              <div className="bg-dark-800/50 rounded-xl p-4 sm:p-5 border border-gray-800/50">
                <StepNumber number={4} label="Befestigungslöcher (optional)" />

                <label
                  className="flex items-center gap-3 cursor-pointer group"
                  onClick={() => {
                    const next = !holesEnabled
                    setHolesEnabled(next)
                    if (!next) {
                      setHolePositions([])
                    } else {
                      if (holeCount === 1) setHolePositions(['top-center'])
                      else if (holeCount === 2) setHolePositions(['top-left', 'top-right'])
                      else setHolePositions(['top-left', 'top-right', 'bottom-left', 'bottom-right'])
                    }
                  }}
                >
                  <div className={`relative w-10 h-5 rounded-full transition-colors ${holesEnabled ? 'bg-primary-500' : 'bg-dark-600'}`}>
                    <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${holesEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </div>
                  <span className="text-sm text-gray-300 group-hover:text-gray-200 transition-colors">Löcher aktivieren</span>
                </label>

                {holesEnabled && (
                  <div className="mt-4 space-y-4">
                    <div>
                      <label className="block text-xs text-gray-400 mb-2">Anzahl</label>
                      <div className="flex gap-2">
                        {([1, 2, 4] as const).map((count) => (
                          <button
                            key={count}
                            onClick={() => handleHoleCountChange(count)}
                            className={`w-10 h-10 rounded-lg text-sm font-bold transition-all ${
                              holeCount === count
                                ? 'bg-primary-500/20 text-primary-400 ring-1 ring-primary-500/50'
                                : 'bg-dark-700/50 text-gray-400 hover:bg-dark-700'
                            }`}
                          >
                            {count}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs text-gray-400 mb-2">Positionen ({holePositions.length}/{holeCount} gewählt)</label>
                      {/* Visual grid representation of hole positions */}
                      <div className="inline-grid grid-cols-3 gap-1.5 bg-dark-700/30 p-2 rounded-lg">
                        {HOLE_POSITIONS.map((pos) => (
                          <button
                            key={pos.id}
                            onClick={() => toggleHolePosition(pos.id)}
                            disabled={!holesEnabled}
                            className={`w-8 h-8 sm:w-10 sm:h-10 rounded-md text-[9px] sm:text-[10px] font-medium transition-all leading-tight ${
                              holePositions.includes(pos.id)
                                ? 'bg-primary-500/30 text-primary-300 ring-1 ring-primary-500/50'
                                : 'bg-dark-700/50 text-gray-500 hover:bg-dark-700 hover:text-gray-400'
                            }`}
                            title={pos.label}
                          >
                            {holePositions.includes(pos.id) ? '●' : '○'}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column: Symbols + Add to Cart */}
            <div className="space-y-5">
              <SymbolPicker onSymbolClick={handleSymbolClick} />

              {/* Character count */}
              <div className="bg-dark-800/50 rounded-xl p-4 border border-gray-800/50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-400">Zeichen</span>
                  <span className={`text-xs font-bold ${text.length >= 8 ? 'text-yellow-400' : 'text-gray-300'}`}>
                    {text.length}/10
                  </span>
                </div>
                <div className="w-full bg-dark-700 rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full transition-all ${
                      text.length >= 9 ? 'bg-red-500' : text.length >= 7 ? 'bg-yellow-500' : 'bg-primary-500'
                    }`}
                    style={{ width: `${(text.length / 10) * 100}%` }}
                  />
                </div>
                <p className="text-[10px] text-gray-500 mt-2">
                  Klicken Sie auf die Platte oder ein Symbol, um Text einzufügen
                </p>
              </div>

              {/* Price + Add to Cart */}
              <div className="bg-dark-800/50 rounded-xl p-4 border border-gray-800/50 space-y-4">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-gray-400">Preis</span>
                  <div className="text-right">
                    <span className="text-2xl font-bold text-white">19,99 &euro;</span>
                    <span className="text-xs text-gray-500 block">inkl. MwSt.</span>
                  </div>
                </div>

                <Button
                  onClick={handleAddToCart}
                  disabled={!text.trim()}
                  size="lg"
                  className="w-full"
                >
                  In den Warenkorb
                </Button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default GiftPlateSection
